import { FindingVersionRef, LabelingService, LabelOptions, Roi } from '@analysis/domain';
import { ListSampleRoisPayload } from '@analysis/use-cases';
import {
  type RoiRepository,
  ListRoisForLabelUseCase,
  GetRoiDetailsUseCase,
} from '@analysis/use-cases';
import { AbortError } from '@shared/use-cases';
import { BehaviorSubject, combineLatest, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, expand, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { take, takeUntil } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  ROI_REPOSITORY,
  RoiStore,
  RoiChangeStore,
  RoiStatsStore,
  MosaicPaginationStore,
  LabelProgress,
  ViewContextStore,
} from '../../stores';
import { concatLatestFrom } from '@ngrx/operators';
import { ProtocolStore } from '@telespot/protocols/ui';

interface FetchContext {
  sampleId: string;
  creatorId: string;
  /**
   * The view generation this batch was requested for. Carried so a response can be
   * recognised as stale and dropped: both the rois it brings and the pagination token it
   * would advance belong to the (sample, analyst) that asked for them.
   */
  generation: string | null;
  options: Record<string, { threshold?: number }>;
  changedRoiIds: Set<string>;
  /**
   * findingId -> the version a save from this page wrote. Rows older than these are the
   * projection's pre-save copies and must not reach the store.
   */
  ownSaves: Map<string, FindingVersionRef>;
}

/**
 * A component store for managing mosaic specific roi features.
 */
export const LIST_ROIS_FOR_LABEL_USE_CASE = new InjectionToken<ListRoisForLabelUseCase>('LIST_ROIS_FOR_LABEL_USE_CASE');
export const GET_ROI_DETAILS_USE_CASE = new InjectionToken<GetRoiDetailsUseCase>('GET_ROI_DETAILS_USE_CASE');

@Injectable()
export class MosaicRoiFeatureEffects {
  private readonly MAX_CONCURRENCY = 5;
  private readonly MAX_CONCURRENT_LABELS = 3;
  private readonly MIN_ITEMS_PER_LOAD = 50;

  // Visible-cell set, observable so reactive effects can combine it with rois$.
  // Each load adds; each cancel removes. The mergeMap project function (and the
  // post-fetch tap) check membership at execution time via wantedRoiIds$.value,
  // so cancellations that arrive while an item is buffered in mergeMap are
  // honored deterministically — no Subject timing race.
  // Blob and roi-label loads share one set because the component drives both
  // from the same cellCreated/cellDestroyed events; they're always in lockstep.
  private readonly wantedRoiIds$ = new BehaviorSubject<ReadonlySet<string>>(new Set());

  // Per-roi AbortController for in-flight requests. cancelLoad* calls .abort()
  // so the HTTP request is cut at the network layer, not just dropped client-side.
  private readonly blobAbortControllers = new Map<string, AbortController>();
  private readonly roiLabelAbortControllers = new Map<string, AbortController>();

  private readonly roiRepository = inject<RoiRepository>(ROI_REPOSITORY);
  private readonly listRoisForLabelUseCase = inject<ListRoisForLabelUseCase>(LIST_ROIS_FOR_LABEL_USE_CASE);
  private readonly getRoiDetailsUseCase = inject<GetRoiDetailsUseCase>(GET_ROI_DETAILS_USE_CASE);
  private readonly roiStore = inject(RoiStore);
  private readonly roiChangeStore = inject(RoiChangeStore);
  private readonly roiStatsStore = inject(RoiStatsStore);
  private readonly paginationStore = inject(MosaicPaginationStore);
  private readonly protocolStore = inject(ProtocolStore);
  private readonly viewContext = inject(ViewContextStore);

  /**
   * The sample AND the analyst come from the view context, never from the analyst store.
   *
   * Reading the analyst directly was the last place a request could describe a generation
   * that had not been published yet: the moment the user picked another analyst,
   * `selectedAnalyst$` emitted and paging could issue a request for the NEW analyst while
   * the pagination still held the previous one's token — before the context producer had
   * published the new generation, so before the transition had cleared it. New creator,
   * old start key, and no guard applicable, because the generation had not moved.
   *
   * Taking both halves from one settled source means a request cannot disagree with the
   * pagination it draws from. The `sampleId` input is kept only as the caller's statement of
   * intent and is checked against the context.
   */
  public readonly loadMore = this.roiStore.effect((input$: Observable<{ sampleId: string; labels: string[] }>) =>
    input$.pipe(
      concatLatestFrom(() => [
        this.viewContext.context$,
        this.protocolStore.options$,
        this.paginationStore.labelProgress$,
        this.roiChangeStore.changedRois$,
        this.roiStatsStore.loading$,
        this.roiStore.roisLoading$,
        this.roiStatsStore.ownSaveVersions$,
      ]),
      map(
        ([
          { sampleId, labels },
          context,
          options,
          labelProgress,
          changedRois,
          statsLoading,
          roisLoading,
          ownSaves,
        ]) => ({
          sampleId,
          labels,
          context,
          options: options || {},
          labelProgress,
          changedRois,
          statsLoading,
          roisLoading,
          ownSaves,
        })
      ),
      filter(({ statsLoading, roisLoading }) => !statsLoading && !roisLoading),
      // A trigger naming a sample the view has already left is not worth issuing.
      filter(({ sampleId, context }) => sampleId === context.sampleId),
      exhaustMap(({ labels, context, options, labelProgress, changedRois, ownSaves }) => {
        const ctx: FetchContext = {
          sampleId: context.sampleId,
          creatorId: context.analyst.id,
          generation: context.generation,
          options,
          changedRoiIds: new Set(changedRois.keys()),
          ownSaves,
        };

        this.roiStore.setLoading(true);

        // Cancel this batch as soon as the view moves to another generation — a late
        // response would merge the previous (sample, analyst)'s rois into a store that has
        // just been cleared for the new one, and advance its pagination with a token that is
        // only valid for the query that produced it.
        //
        // One guard is enough now that the generation carries both halves: the analyst and
        // sample guards it replaces were two views of this same condition, and neither could
        // cover a breadcrumb jump that PRESERVED the analyst.
        const cancelOnGenerationChange$ = this.viewContext.generation$.pipe(
          filter((current) => !!current && current !== ctx.generation)
        );

        const inner$ =
          labels.length === 0
            ? this.fetchUntilMinimum(ctx)
            : from(labels).pipe(
              mergeMap(
                (label) => this.fetchOneLabelBatch(label, labelProgress[label], ctx),
                this.MAX_CONCURRENT_LABELS
              )
            );

        return inner$.pipe(
          takeUntil(cancelOnGenerationChange$),
          finalize(() => this.roiStore.setLoading(false))
        );
      })
    )
  );

  public readonly loadMoreRoiLabels = this.roiStore.effect((roiId$: Observable<string>) =>
    roiId$.pipe(
      tap((roiId) => this.addWantedRoi(roiId)),
      concatLatestFrom(() => this.roiStore.rois$),
      mergeMap(([roiId, rois]) => {
        // Cancelled while buffered in mergeMap — skip before issuing HTTP.
        if (!this.wantedRoiIds$.value.has(roiId)) return EMPTY;

        const existingRoi = rois[roiId];

        if (!existingRoi?.uuid) return EMPTY;

        const controller = new AbortController();
        this.roiLabelAbortControllers.set(roiId, controller);

        return from(this.getRoiDetailsUseCase.execute({ roiId: existingRoi.uuid, signal: controller.signal })).pipe(
          concatLatestFrom(() => [
            this.protocolStore.options$,
            this.roiStore.rois$,
            this.roiStatsStore.ownSaveVersions$,
          ]),
          tap(([roi, options, currentRois, ownSaves]) => {
            if (!roi) return;
            // Cancelled while in flight — don't write a stale merge into the store.
            if (!this.wantedRoiIds$.value.has(roi.id)) return;
            const currentRoi = currentRois[roi.id];
            // The store ROI may have been completed by a label-modifying effect
            // while this request was in flight. additionalLabelsIncluded is
            // sticky-true in roi.store, so an early-return here cannot drop a
            // legitimate later update.
            if (!currentRoi || currentRoi.additionalLabelsIncluded) return;
            const mergedRoi = this.mergeRoiLabels(roi, currentRoi, options);
            // The detail read comes from the projection too, so it can predate a save made
            // here. The store declines those, which also leaves `additionalLabelsIncluded`
            // unset so a later read can still enrich the row.
            this.roiStore.upsertProjectedRois({ rois: new Map([[mergedRoi.id, mergedRoi]]), ownSaves });
          }),
          catchError((error) => {
            // Cancellation is not a UI error — silently swallow.
            if (error instanceof AbortError) return EMPTY;
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => {
            // Only release if this controller is still the registered one — a
            // re-issued load may have overwritten it with a fresh controller.
            if (this.roiLabelAbortControllers.get(roiId) === controller) {
              this.roiLabelAbortControllers.delete(roiId);
            }
          })
        );
      }, this.MAX_CONCURRENCY)
    )
  );

  public readonly loadBlob = this.roiStore.effect((roiId$: Observable<string>) =>
    roiId$.pipe(
      tap((roiId) => this.addWantedRoi(roiId)),
      concatLatestFrom(() => this.roiStore.rois$),
      mergeMap(([roiId, rois]) => {
        // Cancelled while buffered in mergeMap — skip before issuing HTTP.
        if (!this.wantedRoiIds$.value.has(roiId)) return EMPTY;

        const roi = rois[roiId];

        if (!roi || roi.url) return EMPTY;

        // The asset and the region are the whole request now, so a row is
        // fetchable the moment it exists - where the filename-based path had to
        // wait for `cropFile` to land before it could name anything.
        if (!roi.assetId || !roi.coordinates) return EMPTY;

        const controller = new AbortController();
        this.blobAbortControllers.set(roi.id, controller);

        const request = { assetId: roi.assetId, coordinates: roi.coordinates };

        return from(this.roiRepository.getBlob(request, controller.signal)).pipe(
          tap((blob) => {
            // Cancelled while in flight — drop the blob to avoid leaking an
            // object URL for an invisible cell.
            if (!this.wantedRoiIds$.value.has(roi.id)) return;
            this.roiStore.loadBlobUrl({ roiId: roi.id, url: URL.createObjectURL(blob) });
          }),
          catchError(() => EMPTY),
          finalize(() => {
            if (this.blobAbortControllers.get(roi.id) === controller) {
              this.blobAbortControllers.delete(roi.id);
            }
          })
        );
      }, this.MAX_CONCURRENCY)
    )
  );

  public cancelLoadMoreRoiLabels(roiId: string): void {
    this.removeWantedRoi(roiId);
    this.roiLabelAbortControllers.get(roiId)?.abort();
  }

  public cancelLoadBlob(roiId: string): void {
    this.removeWantedRoi(roiId);
    this.blobAbortControllers.get(roiId)?.abort();
  }

  private addWantedRoi(roiId: string): void {
    if (this.wantedRoiIds$.value.has(roiId)) return;
    const next = new Set(this.wantedRoiIds$.value);
    next.add(roiId);
    this.wantedRoiIds$.next(next);
  }

  private removeWantedRoi(roiId: string): void {
    if (!this.wantedRoiIds$.value.has(roiId)) return;
    const next = new Set(this.wantedRoiIds$.value);
    next.delete(roiId);
    this.wantedRoiIds$.next(next);
  }

  /**
   * Fingerprint of everything that shapes the `/rois` query for a label, and therefore
   * everything a `nextToken` is bound to. Mirrors the url builder, which omits `scoreMin`
   * entirely when it is falsy — so a threshold moving between 0 and non-zero changes the
   * conditions even though the number "changed by a little".
   */
  private static queryConditions(label: string, ctx: FetchContext): string {
    const scoreMin = ctx.options[label]?.threshold ?? 0;
    return [ctx.sampleId, ctx.creatorId, label, scoreMin ? String(scoreMin) : ''].join('|');
  }

  /**
   * True once the view has moved to another generation than the one that asked for this
   * batch. `null` means no generation is published yet (nothing to contradict), which only
   * happens before the first stats read has been applied.
   */
  private isStale(ctx: FetchContext): boolean {
    const current = this.viewContext.currentGeneration;
    return !!current && !!ctx.generation && current !== ctx.generation;
  }

  private fetchUntilMinimum(ctx: FetchContext): Observable<unknown> {
    return of({ total: 0 }).pipe(
      expand(({ total }) => {
        if (total >= this.MIN_ITEMS_PER_LOAD) return EMPTY;
        return combineLatest([this.paginationStore.nextLabel$, this.paginationStore.labelProgress$]).pipe(
          take(1),
          concatMap(([nextLabel, labelProgress]) => {
            if (!nextLabel) return EMPTY;
            return this.fetchOneLabelBatch(nextLabel, labelProgress[nextLabel], ctx).pipe(
              map((count) => ({ total: total + count }))
            );
          })
        );
      })
    );
  }

  private fetchOneLabelBatch(
    label: string,
    progress: LabelProgress | undefined,
    ctx: FetchContext
  ): Observable<number> {
    // No entry at all means this generation has not been seeded yet: the transition cleared
    // the pagination and `getSampleStats` has not re-initialised it. Paging now would fetch
    // page 1 only for `initializeProgress` to reset the count moments later and fetch it
    // again. `initializeProgress` seeds every protocol label, so a label worth paging always
    // has an entry.
    if (!progress) return EMPTY;

    const conditions = MosaicRoiFeatureEffects.queryConditions(label, ctx);
    // Progress earned under different conditions says nothing about THIS query: its token
    // would be rejected, and its `complete` flag is about another page set entirely.
    const usable = progress.conditions === conditions ? progress : undefined;

    if (usable?.complete) return EMPTY;
    // Don't even ask if the view has already moved on — cancellation is asynchronous, so a
    // batch can still be dispatched from a loop that started under the previous generation.
    if (this.isStale(ctx)) return EMPTY;

    const payload: ListSampleRoisPayload = {
      sampleId: ctx.sampleId,
      creatorId: ctx.creatorId,
      label,
      nextToken: usable?.nextToken,
      scoreMin: ctx.options[label]?.threshold ?? 0,
    };

    return from(this.listRoisForLabelUseCase.execute(payload)).pipe(
      tap(({ items, nextToken }) => {
        // The last word on staleness: `advanceProgress` upserts, so a late response would
        // otherwise re-create the entry the generation change just cleared — and hand its
        // token, valid only for the old sample and creator, to the next query. That is what
        // the backend rejects with "the provided starting key is outside query boundaries".
        if (this.isStale(ctx)) return;

        this.paginationStore.advanceProgress({
          label,
          // Restart the count when the previous progress was for other conditions.
          previousBatch: usable?.batch ?? 0,
          nextToken,
          conditions,
        });
        this.roiStore.mergeMosaicBatch({
          rois: items,
          changedRoiIds: ctx.changedRoiIds,
          // A page read while the projection lags answers with rows from before a save made
          // here, and `changedRoiIds` cannot speak for them because saving clears it. The
          // store decides what to do with them: it is the only place that knows which of
          // those rows it already holds a newer copy of, or removed on that save.
          ownSaves: ctx.ownSaves,
        });
      }),
      map(({ items }) => items.length),
      catchError((error) => {
        this.roiStore.setLoadError(error.message);
        return EMPTY;
      })
    );
  }

  private mergeRoiLabels(incomingRoi: Roi, existingRoi: Roi, options: LabelOptions): Roi {
    const mergedLabels = new Map<string, number>();

    // Existing store labels are already past presentation filters; keep any
    // that are still in the active protocol (no threshold re-check).
    existingRoi.labels?.forEach((score, label) => {
      if (options[label]) mergedLabels.set(label, score);
    });

    const incomingFiltered = LabelingService.filterLabels(incomingRoi, options).labels;
    for (const [label, score] of incomingFiltered) {
      if (!mergedLabels.has(label)) mergedLabels.set(label, score);
    }

    return { ...incomingRoi, labels: mergedLabels, additionalLabelsIncluded: incomingRoi.additionalLabelsIncluded };
  }
}
